import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { DownloadableFile, Figure, Flex, ListMarker, Loading, Modal, Typo } from '../../../ui';
import { storeApi } from '../../../utils/urls';
import { imageChecker } from '../../../helpers/imageChecker';
import ShowAccessTime from '../../webshop/ShopViewer/components/ShowAccessTime';
import Teacher from '../../webshop/ShopViewer/components/Teacher';
import InfoIconSection from '../../webshop/components/InfoIconSection';

const Wrapper = styled.div`
  padding: 2rem 1.5rem;
`;

const AboutSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AboutImage = styled.div`
  max-width: 480px;
  width: 100%;
  margin: 0 auto;
  border-radius: 12px;
  overflow: hidden;
`;

const PackageList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PackageItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background-color: ${(props) => props.theme.colors.NEU_50};
`;

const PackageThumbnail = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const PackageIndex = styled(Typo.Info)`
  width: 20px;
  flex-shrink: 0;
  text-align: center;
  color: ${(props) => props.theme.colors.NEU_500};
  font-weight: 600;
`;

const parseCourseLayout = (payload) => {
  const isBundle = payload.type === 2;

  let layoutData = null;

  if (isBundle) {
    layoutData = payload.layout;
  } else if (payload.settings) {
    let parsedSettings = payload.settings;
    if (typeof parsedSettings === 'string' && parsedSettings.length) {
      parsedSettings = JSON.parse(parsedSettings);
    }
    if (parsedSettings?.layout) {
      layoutData = parsedSettings.layout;
    }
  }

  const coverImage = payload.coverImage?.[0] || null;
  const fallbackImage = coverImage || (payload.coverUrl ? { url: payload.coverUrl } : null);

  let packageItems = [];
  if (isBundle && payload.courses?.length) {
    packageItems = payload.courses.map((item) => {
      const settingsData = item.config;
      let itemLayout = {};
      if (settingsData?.layout && Object.keys(settingsData.layout).length) {
        const { teacher, highlightedInfo, infoIcons, infoPoints, testimonies, introduction, customFile } = settingsData.layout;
        itemLayout = { teacher, highlightedInfo, infoIcons, infoPoints, testimonies, introduction, customFile };
      }
      itemLayout.courseContent = item.toc;
      return { ...item, settings: { ...settingsData, layout: itemLayout } };
    });
  }

  return {
    name: payload.name,
    layout: layoutData,
    fallbackImage,
    isBundle,
    packageItems,
    ucpPeriod: payload.ucpPeriod || null,
    infoPoints: layoutData?.infoPoints || [],
    sectionTitles: layoutData?.sectionTitles || {},
    customFile: layoutData?.customFile && Object.keys(layoutData.customFile).length ? layoutData.customFile : null,
  };
};

const CourseDetailsModal = ({ course, open, onClose }) => {
  const { t } = useTranslation();
  const [courseData, setCourseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visibleTabPanel, setVisibleTabPanel] = useState({ index: 0, value: 'learn' });
  const [navigationItems, setNavigationItems] = useState([]);

  const fetchCourseDetails = useCallback(async (slug, ownerSlug, type) => {
    setLoading(true);
    setCourseData(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(storeApi(), {
        method: 'POST',
        headers: { token: token || '', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          method: 'findBySlug',
          data: {
            slug,
            type: type || 1,
            toc: 1,
            username: ownerSlug,
          },
        }),
      });
      const json = await res.json();
      if (json.data) {
        setCourseData(parseCourseLayout(json.data));
      }
    } catch (err) {
      console.error('CourseDetailsModal: failed to fetch course', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (course && open) {
      fetchCourseDetails(course.slug, course.ownerSlug, course.type);
    }
    if (!open) {
      setCourseData(null);
      setVisibleTabPanel({ index: 0, value: 'learn' });
      setNavigationItems([]);
    }
  }, [course, open, fetchCourseDetails]);

  useEffect(() => {
    if (!courseData) return;

    const tabList = [];
    let index = 0;

    tabList.push({ label: t('course.packageAboutCourse'), value: 'learn', index });
    index++;

    if (courseData.infoPoints?.length) {
      tabList.push({ label: courseData.sectionTitles?.infoPointsTitle || t('webshop.course.infoPointsTitle'), value: 'insights', index });
      index++;
    }

    if (courseData.layout?.teacher && Object.keys(courseData.layout.teacher).length) {
      tabList.push({ label: t('course.packageMeetTeacher'), value: 'teacher', index });
    }

    if (courseData.isBundle && courseData.packageItems?.length) {
      tabList.push({ label: t('webshop.course.coursesIncluded'), value: 'courses', index });
      index++;
    }

    setVisibleTabPanel(tabList[0]);
    setNavigationItems(tabList);
  }, [courseData, t]);

  const handleTabChange = useCallback(
    (e, newValue) => {
      const selected = navigationItems.find((item) => item.index === newValue);
      setVisibleTabPanel(selected || navigationItems[0] || { index: 0, value: 'learn' });
    },
    [navigationItems],
  );

  const layout = courseData?.layout;

  return (
    <Modal
      width='800px'
      open={open}
      onClose={onClose}
      compact
      title={courseData?.name || course?.name}
      handleTabChange={handleTabChange}
      selectedTab={+visibleTabPanel.index}
      navigationItems={navigationItems}
    >
      {loading ? (
        <Wrapper>
          <Loading />
        </Wrapper>
      ) : (
        <>
          <Wrapper hidden={visibleTabPanel.value !== 'learn'}>
            <AboutSection>
              {!!courseData?.fallbackImage && (
                <AboutImage>
                  <Figure.Rectangle>
                    <img src={imageChecker(courseData.fallbackImage)} alt={courseData?.name} loading='lazy' />
                  </Figure.Rectangle>
                </AboutImage>
              )}
              {!!layout?.introduction && <Typo.Text $textAlign='center'>{layout.introduction?.text}</Typo.Text>}
              {!!layout?.highlightedInfo?.title && (
                <Typo.ContentSmallHeader $textAlign='left'>{layout.highlightedInfo.title}</Typo.ContentSmallHeader>
              )}
              {!!layout?.highlightedInfo?.text && <Typo.Text>{layout.highlightedInfo.text}</Typo.Text>}
              {!!layout?.infoIcons?.length && (
                <Flex direction='column' align='flex-start' gap='4'>
                  {layout.infoIcons.map((item, i) => (
                    <ListMarker key={i}>
                      <Typo.Text>{item.title}</Typo.Text>
                    </ListMarker>
                  ))}
                </Flex>
              )}
              {!!courseData?.ucpPeriod && <ShowAccessTime ucpPeriod={courseData.ucpPeriod} />}
              {!!courseData?.customFile && (
                <DownloadableFile.AsButton label={courseData.customFile.label} file={courseData.customFile.file} />
              )}
            </AboutSection>
          </Wrapper>
          {!!courseData?.infoPoints?.length && (
            <Wrapper hidden={visibleTabPanel.value !== 'insights'}>
              <InfoIconSection items={courseData.infoPoints} />
            </Wrapper>
          )}
          <div hidden={visibleTabPanel.value !== 'teacher'}>{!!layout?.teacher && <Teacher teacherInfo={layout.teacher} />}</div>
          {courseData?.isBundle && (
            <Wrapper hidden={visibleTabPanel.value !== 'courses'}>
              <PackageList>
                {courseData.packageItems.map((item, i) => {
                  const thumb = item.coverImage?.[0]?.url || item.coverUrl;
                  return (
                    <PackageItem key={item.id || i}>
                      <PackageIndex>{i + 1}</PackageIndex>
                      {thumb && <PackageThumbnail src={thumb} alt={item.name} loading='lazy' />}
                      <Typo.Text fontWeight='500'>{item.name}</Typo.Text>
                    </PackageItem>
                  );
                })}
              </PackageList>
            </Wrapper>
          )}
        </>
      )}
    </Modal>
  );
};

export default CourseDetailsModal;
